import { MessageSquare, Pencil, Pin, PinOff, Reply, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PersonAvatar } from '@/components/PersonAvatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { ApiError } from '@/core/api';
import { useMembers, type Member } from '@/features/members';
import { useSession } from '@/stores/session';
import type { Comment, CommentReplyPreview } from '../api';
import {
  useComments,
  useContentType,
  useCreateComment,
  useDeleteComment,
  usePinComment,
  useUpdateComment,
} from '../queries';
import { CommentBody } from './CommentBody';
import { MentionTextarea } from './MentionTextarea';

/** Delay of the second scroll to a linked comment, once the page has settled. */
const COMMENT_SCROLL_SETTLE_MS = 600;

interface CommentsPanelProps {
  /** Django model label, e.g. `"crm.deal"` or `"projects.workitem"`. */
  modelLabel: string;
  /** Primary key (as string — Django GFKs store `object_id` as text). */
  objectId: number | string;
  /** Optional title; default "Comments". */
  title?: string;
  /** Restrict @mentions to these members (e.g. the project's members). Falls
   *  back to all org members when omitted. */
  mentionMembers?: Member[];
}

/**
 * Mount on any entity detail page to attach polymorphic comments.
 * Resolves `modelLabel` → ContentType id via the shared `/common/content-types/`
 * endpoint, then drives the `/collab/comments/` viewset (pinned-first ordering,
 * @mention notifications, author-only edit, author/admin delete + pin).
 */
export function CommentsPanel({
  modelLabel,
  objectId,
  title = 'Comments',
  mentionMembers,
}: CommentsPanelProps) {
  const ct = useContentType(modelLabel);
  const params = { content_type: ct.data?.id, object_id: String(objectId) };
  const { data, isPending, isError, error, isFetching, refetch } = useComments(params);
  const create = useCreateComment(params);

  const currentUserId = useSession((s) => s.user?.id ?? null);
  const role = useSession((s) => s.role);
  const isAdmin = role === 'founder' || role === 'super_admin';

  // Fallback display name when the server didn't resolve author_name.
  const { data: membersData } = useMembers();
  const nameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of membersData?.results ?? []) map.set(m.user.id, m.user.full_name);
    return map;
  }, [membersData]);

  const [draft, setDraft] = useState('');
  // The comment the composer replies to (ADR-112); cleared once posted.
  const [replyTo, setReplyTo] = useState<Comment | null>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  // Deep links from notifications end in `#comment-<id>`: scroll that comment
  // into view once it has loaded, and mark it.
  // Once per navigation (`key`), so following the same link again re-scrolls
  // but a refetch (post / edit / pin) doesn't pull the reader back.
  const { hash, key, search } = useLocation();
  const navigate = useNavigate();
  const focusId = Number(/^#comment-(\d+)$/.exec(hash)?.[1] ?? NaN);
  const linkedLoaded = !!data?.results.some((c) => c.id === focusId);
  // A linked comment not in the (cached) page is fetched for once per
  // navigation — e.g. a new comment on the item already open. Only after that
  // does the panel say it can't show it.
  const [checkedFor, setCheckedFor] = useState<string | null>(null);
  const linkedMissing =
    !Number.isNaN(focusId) && !!data && !linkedLoaded && !isFetching && checkedFor === key;
  const refetchedFor = useRef<string | null>(null);
  useEffect(() => {
    // Wait for a fetch already running (a stale cache refreshing on mount).
    if (Number.isNaN(focusId) || !data || isFetching || linkedLoaded) return;
    if (refetchedFor.current === key) return;
    refetchedFor.current = key;
    void refetch().finally(() => setCheckedFor(key));
  }, [data, focusId, isFetching, key, linkedLoaded, refetch]);
  const scrolledFor = useRef<string | null>(null);
  const settleTimer = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (Number.isNaN(focusId) || scrolledFor.current === key) return;
    if (!data?.results.some((c) => c.id === focusId)) return;
    scrolledFor.current = key;
    const target = () => document.getElementById(`comment-${focusId}`);
    const scroll = () => target()?.scrollIntoView?.({ block: 'center' });
    scroll();
    // Move focus there too, so keyboard / screen-reader users land on it.
    target()?.focus({ preventScroll: true });
    // Sections above the panel (relations, images, embeds) may still be
    // loading and push the comment down — scroll once more when they settle.
    window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(scroll, COMMENT_SCROLL_SETTLE_MS);
  }, [data, focusId, key]);
  useEffect(() => () => window.clearTimeout(settleTimer.current), []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed || !ct.data) return;
    try {
      await create.mutateAsync({
        content_type: ct.data.id,
        object_id: String(objectId),
        body: trimmed,
        ...(replyTarget ? { reply_to: replyTarget.id } : {}),
      });
      setDraft('');
      setReplyTo(null);
    } catch {
      // Shown below the form (field errors) or as a toast (the hook's onError).
    }
  }
  // e.g. `object_id` "Object does not exist." once the item is hidden from you (BE-ADR-098).
  const createFieldError = Object.values((create.error as ApiError | null)?.fieldErrors ?? {}).flat()[0];

  if (ct.isError) {
    return (
      <p className="text-destructive text-sm">
        Could not resolve content type <code>{modelLabel}</code>:{' '}
        {(ct.error as unknown as ApiError)?.message ?? 'unknown error'}
      </p>
    );
  }

  const comments = data?.results ?? [];
  // A reply target deleted meanwhile (by anyone) drops out of the composer —
  // posting would only be refused.
  const replyTarget = replyTo && comments.some((c) => c.id === replyTo.id) ? replyTo : null;
  // Pinned comments sit in their own block, and only there; the thread below
  // reads strictly by time, so a reply never comes before what it answers.
  const pinned = comments.filter((c) => c.is_pinned);
  // The API already orders by time after the pinned ones; sorting again keeps
  // that true however the list was produced (a cache update, say).
  const thread = comments
    .filter((c) => !c.is_pinned)
    .sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
  const nameFor = (c: Comment) =>
    c.author_name ?? (c.author ? (nameById.get(c.author) ?? 'Someone') : 'System');

  function startReply(c: Comment) {
    setReplyTo(c);
    composerRef.current?.focus();
  }

  // The quote line jumps to the original through the `#comment-<id>` deep link
  // (scroll, focus, fetch-once when it isn't loaded), keeping the query string.
  function jumpTo(id: number) {
    navigate({ search, hash: `comment-${id}` });
  }

  const renderItem = (c: Comment) => (
    <CommentItem
      key={c.id}
      comment={c}
      params={params}
      name={nameFor(c)}
      canEdit={c.author != null && c.author === currentUserId}
      canModerate={(c.author != null && c.author === currentUserId) || isAdmin}
      mentionMembers={mentionMembers}
      highlighted={c.id === focusId}
      onReply={() => startReply(c)}
      onJump={jumpTo}
    />
  );

  return (
    <section className="space-y-3" aria-label={title}>
      <h3 className="flex items-center gap-2 text-sm font-semibold">
        <MessageSquare className="text-muted-foreground size-4" />
        {title}
        {comments.length ? (
          <span className="text-muted-foreground text-xs tabular-nums">({comments.length})</span>
        ) : null}
      </h3>

      <form onSubmit={submit} className="space-y-2">
        {replyTarget ? (
          <div className="bg-muted/60 text-muted-foreground flex items-center gap-2 rounded-md px-2 py-1 text-xs">
            <Reply aria-hidden className="size-3.5 shrink-0" />
            <span className="min-w-0 flex-1 truncate">
              Replying to <span className="text-foreground font-medium">{nameFor(replyTarget)}</span>
            </span>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label="Cancel reply"
              title="Cancel reply"
              className="size-5"
              onClick={() => setReplyTo(null)}
            >
              <X className="size-3" />
            </Button>
          </div>
        ) : null}
        <MentionTextarea
          textareaRef={composerRef}
          value={draft}
          onChange={(value) => {
            setDraft(value);
            if (create.error) create.reset(); // a refused post's error goes once you edit
          }}
          placeholder="Comment… use @ to mention a teammate"
          ariaLabel="New comment"
          members={mentionMembers}
        />
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={!draft.trim() || create.isPending || !ct.data}>
            {create.isPending ? 'Posting…' : 'Post'}
          </Button>
        </div>
        {createFieldError ? (
          <p role="alert" className="text-destructive text-xs">
            {createFieldError}
          </p>
        ) : null}
      </form>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load comments.'}
        </p>
      ) : isPending || ct.isPending ? (
        <Skeleton className="h-16 w-full" />
      ) : comments.length ? (
        <div className="space-y-3">
          {pinned.length ? (
            <section aria-label="Pinned comments" className="space-y-1.5">
              <h4 className="text-muted-foreground flex items-center gap-1 text-xs font-medium tracking-wide uppercase">
                <Pin aria-hidden className="size-3" />
                Pinned
              </h4>
              <ul className="space-y-2">{pinned.map(renderItem)}</ul>
            </section>
          ) : null}
          {thread.length ? <ul className="space-y-2">{thread.map(renderItem)}</ul> : null}
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">No comments yet.</p>
      )}
      {data?.truncated ? (
        <p role="status" className="text-muted-foreground text-xs">
          Showing the first {data.results.length} of {data.count} comments.
        </p>
      ) : null}
      {linkedMissing ? (
        <p role="status" className="text-muted-foreground text-xs">
          The comment you followed isn&rsquo;t shown here — it may have been deleted.
        </p>
      ) : null}
    </section>
  );
}

interface CommentItemProps {
  comment: Comment;
  params: { content_type?: number; object_id?: string };
  name: string;
  canEdit: boolean;
  canModerate: boolean;
  mentionMembers?: Member[];
  highlighted?: boolean;
  onReply: () => void;
  /** Go to another comment of this thread (the one a reply quotes). */
  onJump: (id: number) => void;
}

function CommentItem({
  comment: c,
  params,
  name,
  canEdit,
  canModerate,
  mentionMembers,
  highlighted = false,
  onReply,
  onJump,
}: CommentItemProps) {
  const update = useUpdateComment(params);
  const pin = usePinComment(params);
  const remove = useDeleteComment(params);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(c.body);

  async function save() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    await update.mutateAsync({ id: c.id, body: trimmed });
    setEditing(false);
  }

  return (
    <li
      id={`comment-${c.id}`}
      // The comment a notification linked to: focusable (not tabbable) and
      // announced as the current one.
      tabIndex={highlighted ? -1 : undefined}
      aria-current={highlighted ? 'true' : undefined}
      className={cn(
        'hover:bg-muted/40 group scroll-mt-4 rounded-md border p-3 focus:outline-none',
        highlighted && 'border-primary ring-primary/30 ring-2'
      )}
    >
      <div className="text-muted-foreground mb-1 flex items-center justify-between gap-2 text-xs">
        <span className="flex items-center gap-1.5">
          <PersonAvatar name={name} photo={c.author_photo} />
          {c.is_pinned ? <Pin className="text-primary size-3" aria-label="Pinned" /> : null}
          <span className="text-foreground font-medium">{name}</span> ·{' '}
          {new Date(c.created).toLocaleString()}
        </span>
        {!editing ? (
          <span className="flex items-center gap-0.5 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
            <Button
              size="icon"
              variant="ghost"
              aria-label="Reply to comment"
              title="Reply"
              className="size-6"
              onClick={onReply}
            >
              <Reply className="size-3.5" />
            </Button>
            {canModerate ? (
              <Button
                size="icon"
                variant="ghost"
                aria-label={c.is_pinned ? 'Unpin comment' : 'Pin comment'}
                title={c.is_pinned ? 'Unpin' : 'Pin'}
                className="size-6"
                onClick={() => pin.mutate(c.id)}
              >
                {c.is_pinned ? <PinOff className="size-3.5" /> : <Pin className="size-3.5" />}
              </Button>
            ) : null}
            {canEdit ? (
              <Button
                size="icon"
                variant="ghost"
                aria-label="Edit comment"
                title="Edit"
                className="size-6"
                onClick={() => {
                  setDraft(c.body);
                  setEditing(true);
                }}
              >
                <Pencil className="size-3.5" />
              </Button>
            ) : null}
            {canModerate ? (
              <Button
                size="icon"
                variant="ghost"
                aria-label="Delete comment"
                title="Delete"
                className="size-6"
                onClick={() => remove.mutate(c.id)}
              >
                <Trash2 className="size-3.5" />
              </Button>
            ) : null}
          </span>
        ) : null}
      </div>

      {editing ? (
        <div className="space-y-2">
          <MentionTextarea
            value={draft}
            onChange={setDraft}
            ariaLabel="Edit comment"
            members={mentionMembers}
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={!draft.trim() || update.isPending}>
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {c.reply_to_preview ? <QuoteLine preview={c.reply_to_preview} onJump={onJump} /> : null}
          <CommentBody body={c.body} />
        </>
      )}
    </li>
  );
}

/** The Discord-style line above a reply: who it answers and what they said,
 *  one muted line; it jumps to the original. A deleted original can't be
 *  opened, so it says so instead. */
function QuoteLine({
  preview,
  onJump,
}: {
  preview: CommentReplyPreview;
  onJump: (id: number) => void;
}) {
  if (preview.is_deleted) {
    return (
      <p className="text-muted-foreground mb-1 border-l-2 pl-2 text-xs italic">
        Original comment was deleted
      </p>
    );
  }
  const id = preview.id;
  const name = preview.author_name ?? 'Someone';
  return (
    <button
      type="button"
      onClick={() => onJump(id)}
      aria-label={`Reply to ${name}: ${preview.excerpt}`}
      className="text-muted-foreground hover:text-foreground hover:border-primary/60 mb-1 flex w-full min-w-0 items-center gap-1.5 border-l-2 pl-2 text-left text-xs"
    >
      <PersonAvatar
        name={name}
        photo={preview.author_photo}
        className="size-4 [&_[data-slot=avatar-fallback]]:text-[8px]"
      />
      <span className="text-foreground/80 shrink-0 font-medium">{name}</span>
      <span className="min-w-0 truncate">{preview.excerpt}</span>
    </button>
  );
}
